import {
  type ChangeEvent,
  type DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/utils';

type UseFileDropZoneParams = {
  open: boolean;
  onFileSelected: (file: File) => void;
};

export function useFileDropZone({
  open,
  onFileSelected,
}: UseFileDropZoneParams) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  useEffect(() => {
    if (!open) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  }, [open]);

  const onFileChosen = useCallback(
    (fileList: FileList | null) => {
      const file = fileList?.[0];
      if (file) {
        onFileSelected(file);
      }
    },
    [onFileSelected],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFileChosen(event.target.files);
    },
    [onFileChosen],
  );

  const handleDropZoneDragEnter = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current += 1;
      setIsDragging(true);
    },
    [],
  );

  const handleDropZoneDragLeave = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current -= 1;

      if (dragDepth.current <= 0) {
        dragDepth.current = 0;
        setIsDragging(false);
      }
    },
    [],
  );

  const handleDropZoneDragOver = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
    },
    [],
  );

  const handleDropZoneDrop = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current = 0;
      setIsDragging(false);
      onFileChosen(event.dataTransfer.files);
    },
    [onFileChosen],
  );

  const dropZoneClassName = cn(
    'flex min-h-[160px] w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-6 transition-colors',
    isDragging
      ? 'border-primary bg-primary/5'
      : 'border-muted-foreground/25 hover:border-muted-foreground/50',
  );

  return {
    dropZoneClassName,
    fileInputRef,
    handleDropZoneDragEnter,
    handleDropZoneDragLeave,
    handleDropZoneDragOver,
    handleDropZoneDrop,
    onFileInputChange,
    openFilePicker,
  };
}
